import type { StaffSummary } from '@school-erp/types';
import { SALARY_KINDS } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { formatMinor, parseMajor } from '../../lib/money';

interface Component {
  id: string;
  name: string;
  kind: 'earning' | 'deduction';
  isActive: boolean;
}
interface Salary {
  id: string;
  effectiveFrom: string;
  basicMinor: number;
  components: { componentId: string; name: string; kind: string; amountMinor: number }[];
}

export function SalariesTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const manage = can('salary.manage', schoolId);
  const [staffId, setStaffId] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState('earning');
  const [form, setForm] = useState({ effectiveFrom: '', basic: '' });
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const school = useQuery({
    queryKey: ['schools', schoolId],
    queryFn: () => api<{ currencyCode: string }>(`/schools/${schoolId}`),
  });
  const currency = school.data?.currencyCode ?? 'USD';
  const components = useQuery({
    queryKey: ['hr', schoolId, 'components'],
    queryFn: () => api<Component[]>(`/schools/${schoolId}/salary-components`),
  });
  const staff = useQuery({
    queryKey: ['hr', schoolId, 'staff'],
    queryFn: () =>
      apiList<StaffSummary>('/staff', { query: { schoolId, pageSize: 100 } }).then((r) => r.data),
  });
  const history = useQuery({
    queryKey: ['hr', schoolId, 'salary', staffId],
    queryFn: () => api<Salary[]>(`/schools/${schoolId}/staff/${staffId}/salary`),
    enabled: !!staffId,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['hr', schoolId] });
  const addComponent = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/salary-components`, { method: 'POST', body }),
    onSuccess: () => {
      setName('');
      setMessage(null);
      refresh();
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const save = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/staff/${staffId}/salary`, { method: 'POST', body }),
    onSuccess: () => {
      setMessage({ kind: 'ok', text: t('hr.salarySaved') });
      setForm({ effectiveFrom: '', basic: '' });
      setAmounts({});
      refresh();
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  const submit = () => {
    const basicMinor = parseMajor(form.basic, currency);
    const lines: { componentId: string; amountMinor: number }[] = [];
    for (const [componentId, text] of Object.entries(amounts)) {
      if (text.trim() === '') continue;
      const minor = parseMajor(text, currency);
      if (minor === null || minor <= 0) return setError(t('fees.invalidAmount'));
      lines.push({ componentId, amountMinor: minor });
    }
    if (basicMinor === null || basicMinor <= 0 || !form.effectiveFrom)
      return setError(t('fees.invalidAmount'));
    setError(null);
    setMessage(null);
    save.mutate({ effectiveFrom: form.effectiveFrom, basicMinor, components: lines });
  };

  return (
    <section aria-label={t('hr.tab.salaries')} className="space-y-6">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t('hr.components')}</h2>
        <ul className="flex flex-wrap gap-2 text-sm">
          {components.data?.map((c) => (
            <li key={c.id} className="rounded border border-slate-200 bg-white px-3 py-1">
              {c.name}{' '}
              <span className="text-xs text-slate-500">
                ({t(`hr.kind.${c.kind}` as MessageKey)})
              </span>
            </li>
          ))}
        </ul>
        {manage && (
          <form
            noValidate
            aria-label={t('hr.addComponent')}
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) addComponent.mutate({ name: name.trim(), kind });
            }}
            className="grid items-end gap-4 sm:grid-cols-3"
          >
            <TextField
              label={t('hr.componentName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <div>
              <label htmlFor="component-kind" className="block text-sm font-medium">
                {t('hr.kind')}
              </label>
              <select
                id="component-kind"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                className={selectClass}
              >
                {SALARY_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`hr.kind.${k}` as MessageKey)}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" className={secondaryButton} disabled={addComponent.isPending}>
              {t('hr.addComponent')}
            </button>
          </form>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <label htmlFor="salary-staff" className="block text-sm font-medium">
            {t('hr.staff')}
          </label>
          <select
            id="salary-staff"
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
            className={selectClass}
          >
            <option value="">—</option>
            {staff.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.firstName} {s.lastName}
              </option>
            ))}
          </select>
        </div>
        {staffId && history.data?.length === 0 && (
          <p className="text-slate-600">{t('hr.noSalary')}</p>
        )}
        <ul className="space-y-2 text-sm">
          {history.data?.map((h) => (
            <li key={h.id} className="rounded-lg border border-slate-200 bg-white p-3">
              <strong>{h.effectiveFrom}</strong> · {t('hr.basic')}{' '}
              {formatMinor(h.basicMinor, currency)}
              {h.components.map((c) => ` · ${c.name} ${formatMinor(c.amountMinor, currency)}`)}
            </li>
          ))}
        </ul>
        {staffId && manage && (
          <form
            noValidate
            aria-label={t('hr.setSalary')}
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
            className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                label={t('hr.effectiveFrom')}
                type="date"
                value={form.effectiveFrom}
                onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
              />
              <TextField
                label={`${t('hr.basic')} (${currency})`}
                inputMode="decimal"
                value={form.basic}
                error={error}
                onChange={(e) => setForm({ ...form, basic: e.target.value })}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {components.data
                ?.filter((c) => c.isActive)
                .map((c) => (
                  <TextField
                    key={c.id}
                    label={`${c.name} (${currency})`}
                    inputMode="decimal"
                    value={amounts[c.id] ?? ''}
                    onChange={(e) => setAmounts({ ...amounts, [c.id]: e.target.value })}
                  />
                ))}
            </div>
            <button type="submit" className={primaryButton} disabled={save.isPending}>
              {t('hr.saveSalary')}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
